/**
 * Putting action - put objects in containers or on supporters
 *
 * This action handles putting objects into containers or onto supporters.
 * It determines the appropriate preposition based on the target's traits.
 *
 * Uses four-phase pattern with interceptor support (ADR-118):
 * 1. validate: preValidate hook → standard checks → postValidate hook
 * 2. execute: standard mutation → postExecute hook
 * 3. blocked: onBlocked hook (if validation failed)
 * 4. report: standard events → postReport hook (additional effects)
 *
 * Supports multi-object commands:
 * - "put all in box" - puts all carried items in box
 * - "put all but X in box" - puts all except specified items
 * - "put X and Y in box" - puts multiple specified items
 */

import { Action, ActionContext, ValidationResult } from '../../enhanced-types';
import { ActionMetadata } from '../../../validation';
import { ScopeLevel } from '../../../scope/types';
import { ISemanticEvent } from '@sharpee/core';
import {
  TraitType,
  ContainerBehavior,
  SupporterBehavior,
  OpenableBehavior,
  IAddItemResult,
  IAddItemToSupporterResult,
  IFEntity,
  getInterceptorForAction,
  ActionInterceptor,
  InterceptorSharedData,
  applyInterceptorReportResult
} from '@sharpee/world-model';
import { captureEntitySnapshot } from '../../base/snapshot-utils';
import { IFActions } from '../../constants';
import { PuttingMessages } from './putting-messages';

// Import types
import { getPuttingSharedData, PuttingSharedData, PuttingItemResult } from './putting-types';

// Import multi-object helpers
import { isMultiObjectCommand, expandMultiObject } from '../../../helpers/multi-object-handler';
import { entityInfoFrom } from '../../../utils';

// ============================================================================
// Helper Functions (standalone to avoid `this` issues in object literal)
// ============================================================================

/**
 * Determine the target preposition based on preposition text and target type
 */
function determineTargetPreposition(
  preposition: string | undefined,
  target: IFEntity
): { targetPreposition: 'in' | 'on'; error?: string; params?: Record<string, unknown> } {
  const isContainer = target.has(TraitType.CONTAINER);
  const isSupporter = target.has(TraitType.SUPPORTER);

  if (preposition) {
    // User specified a preposition
    if ((preposition === 'in' || preposition === 'into' || preposition === 'inside') && isContainer) {
      return { targetPreposition: 'in' };
    } else if ((preposition === 'on' || preposition === 'onto') && isSupporter) {
      return { targetPreposition: 'on' };
    } else {
      // Mismatched preposition
      if (preposition === 'in' || preposition === 'into' || preposition === 'inside') {
        return {
          targetPreposition: 'in',
          error: PuttingMessages.NOT_CONTAINER,
          params: { destination: entityInfoFrom(target) }
        };
      } else {
        return {
          targetPreposition: 'on',
          error: PuttingMessages.NOT_SURFACE,
          params: { destination: entityInfoFrom(target) }
        };
      }
    }
  } else {
    // Auto-determine based on target type (prefer container over supporter)
    if (isContainer) {
      return { targetPreposition: 'in' };
    } else if (isSupporter) {
      return { targetPreposition: 'on' };
    } else {
      return {
        targetPreposition: 'in',
        error: PuttingMessages.NOT_CONTAINER,
        params: { destination: entityInfoFrom(target) }
      };
    }
  }
}

/**
 * Validate putting a single entity into/onto target
 */
function validateSingleEntity(
  context: ActionContext,
  item: IFEntity,
  target: IFEntity,
  preposition: string | undefined
): ValidationResult & { targetPreposition?: 'in' | 'on' } {
  // Prevent putting something inside/on itself
  if (item.id === target.id) {
    const messageId = preposition === 'on' ? PuttingMessages.CANT_PUT_ON_ITSELF : PuttingMessages.CANT_PUT_IN_ITSELF;
    return {
      valid: false,
      error: messageId,
      params: { item: entityInfoFrom(item) }
    };
  }

  // Check if item is already in/on target
  if (context.world.getLocation(item.id) === target.id) {
    const relation = target.has(TraitType.SUPPORTER) ? 'on' : 'in';
    return {
      valid: false,
      error: PuttingMessages.ALREADY_THERE,
      params: {
        item: entityInfoFrom(item),
        relation: relation,
        destination: entityInfoFrom(target)
      }
    };
  }

  // Determine the target preposition
  const { targetPreposition, error, params } = determineTargetPreposition(preposition, target);
  if (error) {
    return { valid: false, error, params };
  }

  // Container-specific checks
  if (targetPreposition === 'in') {
    // Check if container is open
    if (target.has(TraitType.OPENABLE) && !OpenableBehavior.isOpen(target)) {
      return {
        valid: false,
        error: PuttingMessages.CONTAINER_CLOSED,
        params: { container: entityInfoFrom(target) }
      };
    }

    // Check capacity
    if (!ContainerBehavior.canAccept(target, item, context.world)) {
      return {
        valid: false,
        error: PuttingMessages.NO_ROOM,
        params: { container: entityInfoFrom(target) }
      };
    }
  }

  // Supporter-specific checks
  if (targetPreposition === 'on') {
    if (!SupporterBehavior.canAccept(target, item, context.world)) {
      return {
        valid: false,
        error: PuttingMessages.NO_SPACE,
        params: { surface: entityInfoFrom(target) }
      };
    }
  }

  return { valid: true, targetPreposition };
}

/**
 * Validate a multi-object command (put all in box, put X and Y on table)
 */
function validateMultiObject(context: ActionContext): ValidationResult {
  const target = context.command.indirectObject?.entity;
  const preposition = context.command.parsed.structure.preposition?.text;

  // Must have a destination
  if (!target) {
    return { valid: false, error: PuttingMessages.NO_DESTINATION };
  }

  // For putting, scope is 'carried' - only put things we're holding
  const items = expandMultiObject(context, { scope: 'carried' });

  if (items.length === 0) {
    return { valid: false, error: PuttingMessages.NOTHING_TO_PUT };
  }

  // Validate each entity, store results
  const results: PuttingItemResult[] = items.map(item => {
    const validation = validateSingleEntity(context, item.entity, target, preposition);
    return {
      entity: item.entity,
      success: validation.valid,
      error: validation.valid ? undefined : validation.error,
      errorParams: validation.valid ? undefined : validation.params,
      targetPreposition: validation.targetPreposition
    };
  });

  // Store results for execute/report phases
  const sharedData = getPuttingSharedData(context);
  sharedData.multiObjectResults = results;

  // Valid if at least one can be put
  const anySuccess = results.some(r => r.success);
  if (!anySuccess) {
    // All failed - return the first error
    return { valid: false, error: results[0].error, params: results[0].errorParams };
  }

  return { valid: true };
}

/**
 * Execute putting a single entity
 */
function executeSingleEntity(
  context: ActionContext,
  item: IFEntity,
  target: IFEntity,
  result: PuttingItemResult,
  targetPreposition: 'in' | 'on'
): void {
  result.targetPreposition = targetPreposition;

  // Delegate to appropriate behavior
  if (targetPreposition === 'in') {
    const putResult: IAddItemResult = ContainerBehavior.addItem(target, item, context.world);
    result.putResult = putResult;
  } else {
    const putResult: IAddItemToSupporterResult = SupporterBehavior.addItem(target, item, context.world);
    result.putResult = putResult;
  }

  // Actually move the item to the target
  context.world.moveEntity(item.id, target.id);
}

/**
 * Generate success events for putting a single entity
 *
 * Uses simplified event pattern (ADR-097): domain event carries messageId directly.
 */
function reportSingleSuccess(
  context: ActionContext,
  item: IFEntity,
  target: IFEntity,
  result: PuttingItemResult,
  events: ISemanticEvent[]
): void {
  const targetPreposition = result.targetPreposition as 'in' | 'on';

  if (targetPreposition === 'in') {
    const params = { item: entityInfoFrom(item), container: entityInfoFrom(target) };
    events.push(context.event('if.event.put_in', {
      // Rendering data (messageId + params for text-service)
      messageId: `${context.action.id}.${PuttingMessages.PUT_IN}`,
      params,
      // Domain data (for event sourcing / handlers)
      itemId: item.id,
      itemName: item.name,
      targetId: target.id,
      targetName: target.name,
      actorId: context.player.id,
      preposition: 'in' as const,
      itemSnapshot: captureEntitySnapshot(item, context.world, true),
      targetSnapshot: captureEntitySnapshot(target, context.world, true)
    }));
  } else {
    const params = { item: entityInfoFrom(item), surface: entityInfoFrom(target) };
    events.push(context.event('if.event.put_on', {
      // Rendering data (messageId + params for text-service)
      messageId: `${context.action.id}.${PuttingMessages.PUT_ON}`,
      params,
      // Domain data (for event sourcing / handlers)
      itemId: item.id,
      itemName: item.name,
      targetId: target.id,
      targetName: target.name,
      actorId: context.player.id,
      preposition: 'on' as const,
      itemSnapshot: captureEntitySnapshot(item, context.world, true),
      targetSnapshot: captureEntitySnapshot(target, context.world, true)
    }));
  }
}

/**
 * Generate blocked event for a single entity that couldn't be put
 *
 * Uses simplified event pattern (ADR-097): domain event carries messageId directly.
 */
function reportSingleBlocked(
  context: ActionContext,
  item: IFEntity,
  target: IFEntity,
  error: string,
  errorParams: Record<string, unknown> | undefined,
  events: ISemanticEvent[]
): void {
  events.push(context.event('if.event.put_blocked', {
    // Rendering data — EntityInfo for the formatter chain (ADR-158)
    messageId: `${context.action.id}.${error}`,
    params: { ...errorParams, item: entityInfoFrom(item), destination: entityInfoFrom(target) },
    // Domain data — strings for handlers
    itemId: item.id,
    itemName: item.name,
    targetId: target.id,
    targetName: target.name,
    reason: error
  }));
}

// ============================================================================
// Action Definition
// ============================================================================

export const puttingAction: Action & { metadata: ActionMetadata } = {
  id: IFActions.PUTTING,

  // Default scope requirements for this action's slots
  defaultScope: {
    item: ScopeLevel.REACHABLE,  // REACHABLE allows implicit take
    target: ScopeLevel.REACHABLE
  },

  requiredMessages: [
    'no_target',
    'no_destination',
    'not_held',
    'not_container',
    'not_surface',
    'container_closed',
    'already_there',
    'put_in',
    'put_on',
    'cant_put_in_itself',
    'cant_put_on_itself',
    'no_room',
    'no_space',
    'nothing_to_put'
  ],
  group: 'object_manipulation',

  metadata: {
    requiresDirectObject: true,
    requiresIndirectObject: true,
    directObjectScope: ScopeLevel.REACHABLE,  // REACHABLE allows implicit take
    indirectObjectScope: ScopeLevel.REACHABLE
  },

  validate(context: ActionContext): ValidationResult {
    // Check for multi-object command
    if (isMultiObjectCommand(context)) {
      return validateMultiObject(context);
    }

    // Single object validation
    const actor = context.player;
    const item = context.command.directObject?.entity;
    const target = context.command.indirectObject?.entity;
    const preposition = context.command.parsed.structure.preposition?.text;
    const sharedData = getPuttingSharedData(context);

    // Validate we have an item
    if (!item) {
      return { valid: false, error: PuttingMessages.NO_TARGET };
    }

    // Validate we have a destination
    if (!target) {
      return {
        valid: false,
        error: PuttingMessages.NO_DESTINATION,
        params: { item: entityInfoFrom(item) }
      };
    }

    // Check for interceptor on the target entity (ADR-118)
    // Interceptors are on the container/supporter receiving the item
    const interceptorResult = getInterceptorForAction(target, IFActions.PUTTING);
    const interceptor = interceptorResult?.interceptor;
    const interceptorData: InterceptorSharedData = {
      // Pass item info to interceptor so it knows what's being put
      itemId: item.id,
      itemName: item.name,
      targetId: target.id,
      targetName: target.name,
      preposition: preposition
    };

    // Store for later phases
    sharedData.interceptor = interceptor;
    sharedData.interceptorData = interceptorData;

    // === PRE-VALIDATE HOOK ===
    // Called before standard validation - can block early
    if (interceptor?.preValidate) {
      const result = interceptor.preValidate(target, context.world, actor.id, interceptorData);
      if (result !== null) {
        return {
          valid: result.valid,
          error: result.error,
          params: result.params
        };
      }
    }

    // Item must be carried (or implicitly takeable)
    // This enables "put apple in box" when apple is on the ground
    const carryCheck = context.requireCarriedOrImplicitTake(item);
    if (!carryCheck.ok) {
      return carryCheck.error!;
    }

    // Standard validation
    const standardResult = validateSingleEntity(context, item, target, preposition);
    if (!standardResult.valid) {
      return standardResult;
    }

    // === POST-VALIDATE HOOK ===
    // Called after standard validation passes - can add entity-specific conditions
    if (interceptor?.postValidate) {
      const result = interceptor.postValidate(target, context.world, actor.id, interceptorData);
      if (result !== null) {
        return {
          valid: result.valid,
          error: result.error,
          params: result.params
        };
      }
    }

    return standardResult;
  },

  execute(context: ActionContext): void {
    const actor = context.player;
    const sharedData = getPuttingSharedData(context);
    const target = context.command.indirectObject!.entity!;
    const preposition = context.command.parsed.structure.preposition?.text;

    // Check for multi-object command
    if (sharedData.multiObjectResults) {
      // Determine target preposition once for the batch
      const { targetPreposition } = determineTargetPreposition(preposition, target);

      // Execute for each successful validation
      for (const result of sharedData.multiObjectResults) {
        if (result.success) {
          executeSingleEntity(context, result.entity, target, result, targetPreposition);
        }
      }
      // Note: Multi-object commands don't support interceptors currently
      return;
    }

    // Single object execution
    const item = context.command.directObject!.entity!;

    // Determine the target preposition
    const { targetPreposition } = determineTargetPreposition(preposition, target);

    // Store data for report phase
    sharedData.targetPreposition = targetPreposition;

    // Delegate to appropriate behavior
    if (targetPreposition === 'in') {
      const result: IAddItemResult = ContainerBehavior.addItem(target, item, context.world);
      sharedData.putResult = result;
    } else {
      const result: IAddItemToSupporterResult = SupporterBehavior.addItem(target, item, context.world);
      sharedData.putResult = result;
    }

    // Actually move the item to the target
    context.world.moveEntity(item.id, target.id);

    // === POST-EXECUTE HOOK ===
    // Called after standard execution - can perform additional mutations
    const interceptor = sharedData.interceptor;
    const interceptorData = sharedData.interceptorData || {};
    if (interceptor?.postExecute) {
      interceptor.postExecute(target, context.world, actor.id, interceptorData);
    }
  },

  report(context: ActionContext): ISemanticEvent[] {
    const sharedData = getPuttingSharedData(context);
    const target = context.command.indirectObject!.entity!;
    const events: ISemanticEvent[] = [];

    // Prepend any implicit take events (from requireCarriedOrImplicitTake)
    if (context.sharedData.implicitTakeEvents) {
      events.push(...context.sharedData.implicitTakeEvents);
    }

    // Check for multi-object command
    if (sharedData.multiObjectResults) {
      // Generate events for each item (success and failure)
      for (const result of sharedData.multiObjectResults) {
        if (result.success) {
          reportSingleSuccess(context, result.entity, target, result, events);
        } else {
          reportSingleBlocked(context, result.entity, target, result.error!, result.errorParams, events);
        }
      }
      return events;
    }

    // Single object report
    const item = context.command.directObject!.entity!;
    const targetPreposition = sharedData.targetPreposition as 'in' | 'on';

    // Emit domain event with messageId (simplified pattern - ADR-097)
    if (targetPreposition === 'in') {
      const params = { item: entityInfoFrom(item), container: entityInfoFrom(target) };
      events.push(context.event('if.event.put_in', {
        // Rendering data (messageId + params for text-service)
        messageId: `${context.action.id}.${PuttingMessages.PUT_IN}`,
        params,
        // Domain data (for event sourcing / handlers)
        itemId: item.id,
        itemName: item.name,
        targetId: target.id,
        targetName: target.name,
        actorId: context.player.id,
        preposition: 'in' as const,
        itemSnapshot: captureEntitySnapshot(item, context.world, true),
        targetSnapshot: captureEntitySnapshot(target, context.world, true)
      }));
    } else {
      const params = { item: entityInfoFrom(item), surface: entityInfoFrom(target) };
      events.push(context.event('if.event.put_on', {
        // Rendering data (messageId + params for text-service)
        messageId: `${context.action.id}.${PuttingMessages.PUT_ON}`,
        params,
        // Domain data (for event sourcing / handlers)
        itemId: item.id,
        itemName: item.name,
        targetId: target.id,
        targetName: target.name,
        actorId: context.player.id,
        preposition: 'on' as const,
        itemSnapshot: captureEntitySnapshot(item, context.world, true),
        targetSnapshot: captureEntitySnapshot(target, context.world, true)
      }));
    }

    // === POST-REPORT HOOK ===
    // Apply interceptor's override (replaces if.event.put_in/put_on messageId)
    // and/or emit (additional events). See ISSUE-074.
    const interceptor = sharedData.interceptor;
    const interceptorData = sharedData.interceptorData || {};
    if (interceptor?.postReport) {
      const result = interceptor.postReport(target, context.world, context.player.id, interceptorData);
      const primaryEventType = targetPreposition === 'in' ? 'if.event.put_in' : 'if.event.put_on';
      applyInterceptorReportResult(events, primaryEventType, result, context);
    }

    return events;
  },

  /**
   * Generate events when validation fails
   *
   * Uses simplified event pattern (ADR-097): domain event carries messageId directly.
   */
  blocked(context: ActionContext, result: ValidationResult): ISemanticEvent[] {
    const item = context.command.directObject?.entity;
    const target = context.command.indirectObject?.entity;
    const sharedData = getPuttingSharedData(context);

    // === ON-BLOCKED HOOK ===
    // Called when action is blocked - can provide custom blocked handling
    const interceptor = sharedData.interceptor;
    const interceptorData = sharedData.interceptorData || {};
    if (interceptor?.onBlocked && target && result.error) {
      const customEffects = interceptor.onBlocked(target, context.world, context.player.id, result.error, interceptorData);
      if (customEffects !== null) {
        // Interceptor provided custom blocked effects
        return customEffects.map(effect => context.event(effect.type, effect.payload));
      }
    }

    // Standard blocked handling — EntityInfo for formatter chain (ADR-158)
    return [context.event('if.event.put_blocked', {
      messageId: `${context.action.id}.${result.error}`,
      params: {
        ...result.params,
        item: item ? entityInfoFrom(item) : undefined,
        destination: target ? entityInfoFrom(target) : undefined
      },
      itemId: item?.id,
      itemName: item?.name,
      targetId: target?.id,
      targetName: target?.name,
      reason: result.error
    })];
  }
};
